"use client";

import {
  serializeSelection,
  type DiscoverSelection,
} from "@/components/discover/filters/selection";
import { logger } from "@/lib/logger";
import type { TotalRelation } from "@/openSearchClient";
import type { ClubSource, EventSource } from "@/types/discover";
import { createMapError, MapErrorType } from "@/types/map-errors";
import type { MapBounds } from "@/utils/mapQueryHelpers";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

export type MapEventsResult = {
  events: EventSource[];
  total: number;
  totalRelation: TotalRelation;
};
export type MapClubsResult = {
  clubs: ClubSource[];
  total: number;
  totalRelation: TotalRelation;
};
export type MapDataResult = MapEventsResult | MapClubsResult;

interface UseMapDataQueryOptions {
  bounds: MapBounds | null;
  type: "events" | "clubs";
  selection: DiscoverSelection;
  enabled?: boolean;
}

/**
 * Builds URL search params for a map data API request: the selection's
 * canonical serialization plus bounds and page size. The routes parse the
 * same encoding back with parseSelection.
 * Exported so the map-path wire contract test can exercise the real
 * client -> route translation without a hand-maintained fixture.
 */
export function buildMapQueryParams(
  bounds: MapBounds,
  selection: DiscoverSelection,
): URLSearchParams {
  const params = serializeSelection(selection);
  params.set("topLeftLat", bounds.topLeft.lat.toString());
  params.set("topLeftLng", bounds.topLeft.lng.toString());
  params.set("bottomRightLat", bounds.bottomRight.lat.toString());
  params.set("bottomRightLng", bounds.bottomRight.lng.toString());
  params.set("size", "100");
  return params;
}

const ROUNDING = 1000;

// toFixed drops float noise first: 59.95 * 1000 is 59950.000000000007.
const snap = (value: number, round: (scaled: number) => number): number =>
  round(Number((value * ROUNDING).toFixed(6))) / ROUNDING;

/**
 * Bounds grown outward to 3 decimals (~100 m), so tiny camera moves share a
 * cache entry and the rounded area still holds the whole view.
 */
export function roundBounds({ topLeft, bottomRight }: MapBounds): MapBounds {
  return {
    topLeft: {
      lat: snap(topLeft.lat, Math.ceil),
      lng: snap(topLeft.lng, Math.floor),
    },
    bottomRight: {
      lat: snap(bottomRight.lat, Math.floor),
      lng: snap(bottomRight.lng, Math.ceil),
    },
  };
}

export function boundsKey(bounds: MapBounds): { tl: string; br: string } {
  const { topLeft, bottomRight } = roundBounds(bounds);
  return {
    tl: `${topLeft.lat.toFixed(3)},${topLeft.lng.toFixed(3)}`,
    br: `${bottomRight.lat.toFixed(3)},${bottomRight.lng.toFixed(3)}`,
  };
}

/**
 * Generates a stable query key for React Query based on bounds, type, and selection
 */
function generateQueryKey(
  bounds: MapBounds | null,
  type: "events" | "clubs",
  selection: DiscoverSelection,
): (string | object)[] {
  if (!bounds) return ["map-data", type, "no-bounds"];

  return [
    "map-data",
    type,
    boundsKey(bounds),
    serializeSelection(selection).toString(),
  ];
}

/** GETs a map route. The body is cancelled on an error so the request settles. */
export async function fetchMapRoute(
  type: "events" | "clubs",
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<unknown> {
  const response = await fetch(
    `${process.env.NEXT_PUBLIC_BASE_PATH || ""}/api/map/${type}?${params}`,
    { signal },
  );

  if (!response.ok) {
    await response.body?.cancel();
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Fetches map data from the API
 */
async function fetchMapData(
  bounds: MapBounds,
  type: "events" | "clubs",
  selection: DiscoverSelection,
  signal?: AbortSignal,
): Promise<MapDataResult> {
  return fetchMapRoute(
    type,
    buildMapQueryParams(bounds, selection),
    signal,
  ) as Promise<MapDataResult>;
}

/**
 * React Query hook for fetching map data by bounds
 * Replaces the manual cache implementation in useFetchMapData
 */
export function useMapDataQuery({
  bounds,
  type,
  selection,
  enabled = true,
}: UseMapDataQueryOptions) {
  return useQuery({
    queryKey: generateQueryKey(bounds, type, selection),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      if (!bounds) {
        throw new Error("Bounds are required");
      }

      try {
        return await fetchMapData(bounds, type, selection, signal);
      } catch (error) {
        // Don't log abort errors
        if (error instanceof Error && error.name === "AbortError") {
          throw error;
        }

        const isNetworkError =
          error instanceof TypeError && error.message.includes("fetch");
        const errorType = isNetworkError
          ? MapErrorType.NETWORK_ERROR
          : MapErrorType.API_REQUEST_FAILED;

        const mapError = createMapError(
          errorType,
          `Failed to fetch ${type} data`,
          error,
          { bounds, selection, type },
        );

        logger.error(
          { error, bounds, selection, type },
          `Failed to fetch ${type} data`,
        );

        throw mapError;
      }
    },
    enabled: enabled && bounds !== null,
    staleTime: 30_000, // 30 seconds - matches original cache TTL
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Type guard for events result
 */
export function isEventsResult(data: MapDataResult): data is MapEventsResult {
  return "events" in data;
}

/**
 * Type guard for clubs result
 */
export function isClubsResult(data: MapDataResult): data is MapClubsResult {
  return "clubs" in data;
}
